import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import type { HttpResponse } from '../../http/HttpClient';
import type { Country, State, StationGroupDetail, StationGroupsQueryParams } from '../../models/bff/Geography';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import { filterQueryParams } from '../../../utils/queryParams';

const NAME = 'geography';

/**
 * GeographyRepository class for handling geography-related API operations.
 * Uses the BFF authenticated API client for countries, states, and station groups.
 * Type-safe: Only allows BFF endpoints defined in BFF_ENDPOINTS.
 */
export class GeographyRepository extends BaseRepository<BffEndpoint> {
  static override readonly name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  /**
   * Retrieve all available countries.
   *
   * @returns Promise<HttpResponse<Country[]>> - The list of countries
   */
  getCountries(): Promise<HttpResponse<Country[]>> {
    return this.apiClient.get<Country[]>(BFF_ENDPOINTS.COUNTRIES);
  }

  /**
   * Retrieve all states for a given country.
   *
   * @param countryCode - ISO 3166-1 alpha-2 country code
   * @returns Promise<HttpResponse<State[]>> - The list of states for the country
   */
  getStates(countryCode: string): Promise<HttpResponse<State[]>> {
    return this.apiClient.get<State[]>(
      BFF_ENDPOINTS.STATES,
      { pathParams: { country: countryCode } },
    );
  }

  /**
   * Retrieve station groups (MAC codes) visible to the current tenant.
   *
   * @param filters - Optional filters
   * @returns Promise<HttpResponse<StationGroupDetail[]>> - The list of station groups
   */
  getStationGroups(filters?: StationGroupsQueryParams): Promise<HttpResponse<StationGroupDetail[]>> {
    const filteredParams = filterQueryParams(filters);

    return this.apiClient.get<StationGroupDetail[]>(
      BFF_ENDPOINTS.STATION_GROUPS,
      { query: filteredParams },
    );
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: GeographyRepository;
  }
}
